import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { styled } from "styled-components";
import CloseIcon from "../../../asset/images/new/Close.svg";
import * as s from "../InputPage.styles";
import { AccessoryItem, AnalysisForm } from "../machine";

const ACCESSORY_NAMES: AccessoryName[] = [
  "로터리/써래",
  "쟁기",
  "로더",
  "살포기",
];

const isAccessoryName = (value: string): value is AccessoryName => {
  return (ACCESSORY_NAMES as string[]).includes(value);
};

// formData.implementList → AccessoryFormState 로 변환
const buildAccessoryStateFromForm = (
  implementList: AnalysisForm["implementList"],
  crop?: AnalysisForm["crop"]
): AccessoryFormState => {
  // crop 기준 기본값으로 시작
  const base: AccessoryFormState = { ...getInitialAccessoryState(crop) };

  implementList.forEach((item) => {
    if (!isAccessoryName(item.implement)) return;

    base[item.implement] = {
      price: item.price ?? base[item.implement].price,
      maintenance: item.maintenance ?? base[item.implement].maintenance,
      units: item.units ?? base[item.implement].units,
      periodDays: item.periodDays ?? base[item.implement].periodDays,
      speed: item.speed ?? base[item.implement].speed,
      width: item.width ?? base[item.implement].width,
    };
  });

  return base;
};

type AccessoryName = "로터리/써래" | "쟁기" | "로더" | "살포기";
type AccessoryDetailState = Record<AccessoryName, boolean>;

type AccessoryFormState = Record<
  AccessoryName,
  {
    periodDays: number; // 일수
    speed: number; // 속도
    width: number; // 사용폭
    units: number; // 대수
    price: number; //  단가
    maintenance: number; // 유지비
  }
>;

const baseAccessoryState: AccessoryFormState = {
  "로터리/써래": {
    price: 700,
    maintenance: 30,
    units: 1,
    periodDays: 6,
    speed: 5,
    width: 3.1,
  },
  쟁기: {
    price: 600,
    maintenance: 30,
    units: 1,
    periodDays: 7,
    speed: 9,
    width: 3,
  },
  로더: {
    price: 950,
    maintenance: 70,
    units: 1,
    periodDays: 8,
    speed: 4,
    width: 1.8,
  },
  살포기: {
    price: 450,
    maintenance: 30,
    units: 1,
    periodDays: 20,
    speed: 10,
    width: 2,
  },
};

const getInitialAccessoryState = (crop?: string): AccessoryFormState => {
  // 벼일 때만 기간/속도/폭 다르게
  if (crop === "벼") {
    return {
      "로터리/써래": {
        ...baseAccessoryState["로터리/써래"],
        periodDays: 6,
        speed: 5,
        width: 1.85,
      },
      쟁기: {
        ...baseAccessoryState["쟁기"],
        periodDays: 7,
        speed: 6,
        width: 1.05,
      },
      로더: {
        ...baseAccessoryState["로더"],
        periodDays: 8,
        speed: 4,
        width: 1.8,
      },
      살포기: {
        ...baseAccessoryState["살포기"],
        periodDays: 9,
        speed: 6,
        width: 1.8,
      },
    };
  }

  // 그 외 작물은 기본값
  return baseAccessoryState;
};

interface AccessoryInfo {
  name: AccessoryName;
  detail: { title: string; desc: string }[];
}

const accessoryList: AccessoryInfo[] = [
  {
    name: "로터리/써래",
    detail: [
      {
        title: "단가 수준",
        desc: "약 500만 ~ 900만",
      },
      {
        title: "주요 특징",
        desc: "넓은 면적의 기본 경운 작업에 주로 사용됩니다. 작업폭, 기종(고정식/접이식), 브랜드에 따라 가격 차이가 발생합니다.",
      },
      {
        title: "유지관리",
        desc: "칼날 마모가 빠른 편으로, 매년 1회 이상 교체(약 10~30만원)을 권장합니다.",
      },
    ],
  },
  {
    name: "쟁기",
    detail: [
      {
        title: "단가 수준",
        desc: "약 400만 ~ 800만",
      },
      {
        title: "주요 특징",
        desc: "정지(땅 고르기) 작업에 주로 사용되며 유압식 여부에 따라 가격 차이가 있습니다.",
      },
      {
        title: "유지관리",
        desc: "유지비 부담이 낮으며 주기적인 볼트, 날 점검(연 10~30만원) 정도가 필요합니다.",
      },
    ],
  },
  {
    name: "로더",
    detail: [
      {
        title: "단가 수준",
        desc: "약 700만~1,200만원",
      },
      {
        title: "주요 특징",
        desc: "프론트 로더 기준 단가이며 흙, 비료, 수확물 등의 하역/운반 작업에 사용됩니다. 유압식/기계식 구조 및 작업 용량에 따라 가격이 달라집니다. ",
      },
      {
        title: "유지관리",
        desc: "유압계통이 포함되어 있어 고장 위험이 다소 높고 정비비가 비교적 높습니다 (연 30~70만원)",
      },
    ],
  },
  {
    name: "살포기",
    detail: [
      {
        title: "단가 수준",
        desc: "약 300만~600만원",
      },
      {
        title: "주요 특징",
        desc: "스프레더, 비료살포기 기준이며 비료나 약제를 넓은 면적에 살포하는데 사용됩니다. 자주형 또는 PTO 연결형 등 다양한 형태가 있습니다. ",
      },
    ],
  },
];

interface Props {
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
  isEdit?: boolean;
  onClose: () => void;
}

function AccessoryPickerModal({
  formData,
  setFormData,
  isEdit,
  onClose,
}: Props) {
  const [activeAccessoryList, setActiveAccessoryList] = useState<
    AccessoryName[]
  >(() =>
    formData.implementList.map((i) => i.implement).filter(isAccessoryName)
  );
  const [openDetail, setOpenDetail] = useState<AccessoryDetailState>({
    "로터리/써래": false,
    쟁기: false,
    로더: false,
    살포기: false,
  });

  const [accessoryState, setAccessoryState] = useState<AccessoryFormState>(() =>
    buildAccessoryStateFromForm(formData.implementList, formData.crop)
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleToggleAccessory = (name: AccessoryName) => {
    setActiveAccessoryList((prev) =>
      prev.includes(name) ? prev.filter((i) => i !== name) : [...prev, name]
    );
  };

  const parseNumber = (value: string) => {
    const num = Number(value.replace(/,/g, ""));
    return Number.isNaN(num) ? 0 : num;
  };

  const handleChangePrice = (name: AccessoryName, value: string) => {
    const price = parseNumber(value);
    setAccessoryState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        price,
      },
    }));
  };

  const handleChangeMaintenance = (name: AccessoryName, value: string) => {
    const maintenance = parseNumber(value);
    setAccessoryState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        maintenance,
      },
    }));
  };

  const handleChangeUnits = (name: AccessoryName, delta: number) => {
    setAccessoryState((prev) => {
      const nextUnits = Math.max(1, prev[name].units + delta);
      return {
        ...prev,
        [name]: {
          ...prev[name],
          units: nextUnits,
        },
      };
    });
  };

  const handleReset = () => {
    setActiveAccessoryList([]);
    setAccessoryState(getInitialAccessoryState(formData.crop));
  };

  const handleConfirm = () => {
    const implementsFromModal = activeAccessoryList.map((name) => {
      const state = accessoryState[name];
      return {
        implement: name,
        periodDays: state.periodDays,
        speed: state.speed,
        width: state.width,
        units: state.units,
        price: state.price,
        maintenance: state.maintenance,
      };
    });

    setFormData((prev) => ({
      ...prev,
      implementList: implementsFromModal,
    }));

    onClose();
  };

  return createPortal(
    <Dimmer onClick={onClose} aria-modal role="dialog">
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <img
            src="/asset/images/arrow/backArrowTopNav.svg"
            alt=""
            className="mobile-only"
            onClick={onClose}
          />
          <Title>부속기 {isEdit ? "수정하기" : "추가하기"}</Title>
          <img
            src={CloseIcon}
            alt=""
            className="w-24 hover"
            onClick={onClose}
          />
        </Header>

        <Body>
          {accessoryList.map((a) => {
            const active = activeAccessoryList.includes(a.name);
            const state = accessoryState[a.name];
            return (
              <Item key={a.name}>
                <ItemHeader
                  onClick={() => {
                    handleToggleAccessory(a.name);
                  }}
                >
                  <ItemName>{a.name}</ItemName>
                  <img
                    src={`/asset/images/check/check-${active ? "on" : "off"}.svg`}
                    alt=""
                  />
                </ItemHeader>
                {active && (
                  <>
                    <ItemInner>
                      <InnerRow>
                        <InputField>
                          <div className="label">단가</div>
                          <s.InputWrapper>
                            <s.Input
                              value={state.price || ""}
                              onChange={(e) =>
                                handleChangePrice(a.name, e.target.value)
                              }
                            />
                            <span>만원</span>
                          </s.InputWrapper>
                        </InputField>
                        <InputField>
                          <div className="label">연 유지비</div>
                          <s.InputWrapper>
                            <s.Input
                              value={state.maintenance || ""}
                              onChange={(e) =>
                                handleChangeMaintenance(a.name, e.target.value)
                              }
                            />
                            <span>만원</span>
                          </s.InputWrapper>
                        </InputField>
                      </InnerRow>
                      <ItemCntContainer>
                        <ChanceCntButton
                          onClick={(e) => {
                            e.preventDefault();
                            handleChangeUnits(a.name, -1);
                          }}
                        >
                          <img src="/asset/images/icon/minus.svg" alt="" />
                        </ChanceCntButton>
                        <ItemCnt>{state.units}대</ItemCnt>
                        <ChanceCntButton
                          onClick={(e) => {
                            e.preventDefault();
                            handleChangeUnits(a.name, 1);
                          }}
                        >
                          <img src="/asset/images/icon/plus.svg" alt="" />
                        </ChanceCntButton>
                      </ItemCntContainer>
                    </ItemInner>
                    <DetailSection>
                      <DetailAccordian
                        onClick={() =>
                          setOpenDetail((prev) => ({
                            ...prev,
                            [a.name]: !prev[a.name],
                          }))
                        }
                      >
                        상세 설명 {openDetail[a.name] ? "닫기" : "보기"}
                        <Arrow
                          $open={openDetail[a.name]}
                          src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                          alt=""
                        />
                      </DetailAccordian>
                      {openDetail[a.name] &&
                        a.detail.map((item, idx) => (
                          <Fragment>
                            <DetailItem>
                              <div className="title">{item.title}</div>
                              <div className={idx !== 0 ? "desc" : ""}>
                                {item.desc}
                              </div>
                            </DetailItem>
                          </Fragment>
                        ))}
                    </DetailSection>
                  </>
                )}
              </Item>
            );
          })}
        </Body>
        <Footer>
          {isEdit ? (
            <ResetButton onClick={onClose}>취소</ResetButton>
          ) : (
            <ResetButton onClick={handleReset}>
              <img src="/asset/images/icon/reset.svg" alt="" />
              초기화
            </ResetButton>
          )}
          <ConfirmButton onClick={handleConfirm}>
            {isEdit ? "수정 완료" : "선택완료"}
          </ConfirmButton>
        </Footer>
      </Dialog>
    </Dimmer>,
    document.body
  );
}
export default AccessoryPickerModal;

const Dimmer = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .desktop-only {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .mobile-only {
    @media (min-width: 1024px) {
      display: none;
    }
  }
`;

const Dialog = styled.div`
  width: 640px;
  max-width: calc(100% - 40px);
  height: 80vh;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1023px) {
    width: 100%;
    height: 100%;
    max-height: none;
    max-width: none;
    border-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-bottom: 1px solid rgba(23, 30, 38, 0.1);

  @media (max-width: 1023px) {
    border: none;
  }
`;

const Title = styled.div`
  flex: 1 0 0;
  font-weight: 700;
  font-size: 16px;
  color: #171e26;

  @media (max-width: 1023px) {
    font-weight: 500;
    text-align: center;
  }
`;

const Body = styled.div`
  min-height: 0;
  overflow-y: auto;
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const Item = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  background: #f7f8f9;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  cursor: pointer;
`;

const ItemName = styled.div`
  flex: 1 0 0;
  color: #171e26;
  font-size: 16px;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;

const ItemInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const InnerRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const InputField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  min-width: 0;

  & > .label {
    color: rgba(23, 30, 38, 0.78);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }
`;

const ItemCntContainer = styled.div`
  display: flex;
  padding: 4px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-White, #fff);
`;

const ChanceCntButton = styled.button`
  border: none;
  cursor: pointer;

  display: flex;
  width: 40px;
  height: 40px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-200, #f0f0f0);
`;
const ItemCnt = styled.div`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const DetailAccordian = styled.div`
  cursor: pointer;
  display: flex;
  padding: 8px 0;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  color: rgba(23, 30, 38, 0.56);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
`;

export const Arrow = styled.img<{ $open: boolean }>`
  width: 20px;
  height: 20px;
  aspect-ratio: 1/1;

  transition: transform 200ms ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const DetailItem = styled.div`
  display: flex;
  padding: 4px 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  color: rgba(23, 30, 38, 0.78);
  font-family: "Spoqa Han Sans Neo";
  font-size: 13px;
  font-weight: 500;
  line-height: 145%; /* 18.85px */
  letter-spacing: -0.26px;

  & > .title {
    font-weight: 700;
  }

  & > .desc {
    color: rgba(23, 30, 38, 0.56);
  }
`;

const Footer = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  border-top: 1px solid var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
  background: #fff;
`;
const ResetButton = styled.button`
  border: 0;
  cursor: pointer;

  display: flex;
  width: 120px;
  padding: 14px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-White, #fff);
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const ConfirmButton = styled.button`
  border: 0;
  cursor: pointer;

  display: flex;
  width: 200px;
  padding: 14px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #1e9b6b;
  color: #fff;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */

  &:disabled {
    background: var(--Alias-Color-Text-White-Gray-LightGray-200, #f0f0f0);
    color: var(--Alias-Color-Text-Dark-Text-Disabled, rgba(23, 30, 38, 0.4));
  }
`;
